/**
 * @file svelte 组件大同上报相关工具方法
 *
 * 相当于 vue 组件的 v-report 指令的功能
 *
 * 代码逻辑参考 ui/Component.js 和 @tencent/iwan-act-report
 *
 */

import type { IComponentPropsConfig } from '@/svelte-comp-adapter/interface';

/**
 * 设置 svelte 组件的上报数据 (dt-eid 和 dt-params)
 *
 * @param config 组件配置 config
 * @param el 组件对应的元素
 */
export const setSvelteComponentReportData = (config: IComponentPropsConfig, el: HTMLElement) => {
  const { report, type, modId } = config;
  const { module, click, expose } = report;

  let reportParams: Record<string, any> = {};
  const modifiers: Record<string, boolean> = {};

  if (module) {
    module.comp_type = type;
    reportParams = module;
    modifiers.module = true;
    if (reportParams['模块']) {
      reportParams = reportParams['模块'];
    }
  }

  if (click) {
    click.comp_type = type;
    if (modId) {
      click.f_magic_modid = modId;
    }
    reportParams = click;
    modifiers.click = true;
  }

  if (expose) {
    expose.comp_type = type;
    if (modId) {
      expose.f_magic_modid = modId;
    }
    reportParams = expose;
    modifiers.expose = true;
  }

  const { dtEid, dtParams } = getReportData(type, reportParams, modifiers);
  el.setAttribute('dt-eid', dtEid);
  el.setAttribute('dt-params', dtParams);
};

/**
 * 适用于 svelte 组件的大同上报指令
 *
 * 功能等同于 v-report
 */
export const svelteReportDirective = (el: HTMLElement, value: Record<string, any>) => {
  updateElReportData(el, value);
  return {
    update(newValue: Record<string, any>) {
      updateElReportData(el, newValue);
    },
  };
};

/**
 * 更新组件元素上的上报数据( dt-eid 和 dt-params )
 *
 * @param el 对应的元素
 * @param reportValue 上报数据
 */
const updateElReportData = (el: HTMLElement, reportValue: Record<string, any>) => {
  const preDtParams = el.getAttribute('dt-params') || '';
  const preDtEid = el.getAttribute('dt-eid');
  el.setAttribute('dt-params', objectToQuery({
    ...queryToObject(preDtParams),
    ...queryToObject(getDtParamsStringByValue(reportValue)),
  }));
  const eid = reportValue.eid || preDtEid;
  el.setAttribute('dt-eid', eid);
};

/**
 * 大同上报: 按钮类型的上报
 */
const UNIVERSAL_REPORT_BUTTON_TYPE = 4;
enum ReportType {
  pv = 'pv',
  module = 'module',
  expose = 'expose',
  click = 'click',
  all = 'all',
}

const getReportData = (type: string, v: Record<string, any>, modifiers: Record<string, boolean>) => {
  const value = v;
  const reportType = getReportType(modifiers);

  if (reportType === ReportType.pv || !value) {
    return {
      dtEid: '',
      dtParams: '',
    };
  }

  if (reportType === ReportType.module && value._module) {
    value.mod_title = value._module;
    value.btn_title = value._module;
  }

  let { eid } = value;

  if (!eid) {
    eid = value.f_type === UNIVERSAL_REPORT_BUTTON_TYPE ? 'button' : 'module';
    console.warn(`[sca] 组件 ${type} 的上报参数 'eid' 缺失，使用默认的 'eid': ${eid}`);
  }

  return {
    dtParams: getDtParamsStringByValue(value),
    dtEid: eid,
  };
};

/**
 * 获取上报类型
 *
 * 参考了 vue 指令的属性命名习惯
 *
 * @param reportModifiers 上报指令的 modifiers
 * @returns
 */
const getReportType = (reportModifiers: Record<string, boolean>) => {
  const types = Object.keys(reportModifiers);

  if (types.includes(ReportType.expose) && types.includes(ReportType.click)) {
    return ReportType.all;
  }

  return types[0] || ReportType.all;
};

/**
 * boss 到大同的上报 key 映射表
 */
const bossDtKeyMap: Record<string, string> = {
  f_magic_modid: 'mod_id',
  f_module: 'mod_title',
  f_resource: 'btn_title',
};

/**
 * 将上报对象转换成大同上报参数字符串
 *
 * 这里需要排除一些字段，防止重复上报
 *
 * @param value 上报对象
 * @param excludeKeys 需要排除的 key
 * @returns 大同上报参数字符串
 */
const getDtParamsStringByValue = (value: Record<string, any>, excludeKeys = ['eid']) => {
  const queryArr: string[] = [];
  Object.keys(value)
    .filter(key => !excludeKeys.includes(key))
    .forEach((key) => {
      const mappedKey = bossDtKeyMap[key] || key;
      const kv = `${mappedKey}=${value[key]}`;
      if (!queryArr.includes(kv)) {
        queryArr.push(kv);
      }
    });
  return queryArr.join('&');
};

const queryToObject = (data = '') => {
  const result: Record<string, string> = {};
  data.split('&').forEach((pair) => {
    const [key, value] = pair.split('=');
    if (key) {
      result[key] = value;
    }
  });
  return result;
};

const objectToQuery = (data: Record<string, string>) => {
  const result: string[] = [];
  Object.keys(data).forEach((key) => {
    result.push(`${key}=${data[key]}`);
  });
  return result.join('&');
};
